// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { getRouteParam } from "@selectors";
import * as sharedSelectors from "@modules/shared/selectors";
import type { SpecialContactType } from "@modules/shared/reducer/special-contact-types/types";
import actionCreator from "@modules/service/actionCreator";
import { SectionComponent, GSGrid, Button, View } from "@components";
import {
  AlertCriteriaView,
  AlertContentForm,
  AlertEmailAttachmentsForm,
  AlertAbsenceReasonsView,
  AlertRecipientsView,
  AlertBespokeRecipientsView,
  AlertServiceLengthView,
  AlertSuppression,
  AlertsEditModal,
} from "@modules/service/components";
import type { State } from "@store/reducers";
import type { Alert } from "@modules/service/reducer/alerts/types";

type OwnProps = $Exact<{
  clientId: number,
  data: Alert,
  schema: Object,
}>;

type PropsFromState = $Exact<{
  specialContactTypes: Array<SpecialContactType>,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
  getCurrentAlert: typeof actionCreator.GetAlert,
  getSchema: typeof actionCreator.GetAlertSchema,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function AlertDetailsView(props: Props) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(0);

  const { clientId } = props;
  useEffect(() => {
    props.getSchema({ clientId });
  }, []);

  const toggleEditModal = () => {
    setIsEditModalOpen(prev => !prev);
  };

  const handleEditStep = (step: number) => {
    return () => {
      props.setDraft(props.data);
      setModalStep(step);
      toggleEditModal();
    };
  };

  if (!props.data) {
    return null;
  }

  return (
    <View gap="6">
      {/* Criteria */}
      <SectionComponent
        className="gs-shadow"
        title={<h5>{props.t("ALERT_DETAILS_CRITERIA")}</h5>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(1)}>
            {props.t("EDIT")}
          </Button>
        }
      >
        <AlertCriteriaView data={props.data} />
      </SectionComponent>

      {/* Content */}
      <SectionComponent
        className="gs-shadow"
        title={<h5>{props.t("CONTENT")}</h5>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(2)}>
            {props.t("EDIT")}
          </Button>
        }
      >
        <View gap={4}>
          <AlertContentForm
            data={props.data.content.emailContent}
            schema={props.schema?.properties?.emailContent || {}}
          />
          <AlertEmailAttachmentsForm
            data={props.data.content.attachments}
            schema={props.schema}
            clientId={props.clientId}
            alertId={props.data.id}
          />
        </View>
      </SectionComponent>

      {/* Absence Reason */}
      <SectionComponent
        className="gs-shadow"
        title={<h5>{props.t("ABSENCE_REASONS")}</h5>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(3)}>
            {props.t("EDIT")}
          </Button>
        }
      >
        <AlertAbsenceReasonsView data={props.data.content} />
      </SectionComponent>
      {/* Alert suppression */}
      <SectionComponent>
        <AlertSuppression clientId={props.clientId} alertId={props.data.id} />
      </SectionComponent>

      {/* Recepients */}
      <SectionComponent
        className="gs-shadow"
        title={<h5>{props.t("RECIPIENTS")}</h5>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(4)}>
            {props.t("EDIT")}
          </Button>
        }
      >
        <View gap={5}>
          <AlertRecipientsView
            data={props.data.content}
            specialContactTypes={props.specialContactTypes}
          />
          <AlertBespokeRecipientsView data={props.data.content} />
        </View>
      </SectionComponent>

      {/* Staff groups */}
      <SectionComponent
        className="gs-shadow"
        title={<h5>{props.t("ALERT_DETAILS_STAFF_GROUPS")}</h5>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(5)}>
            {props.t("EDIT")}
          </Button>
        }
      >
        <GSGrid
          columns={[
            {
              name: props.t("ALERT_DETAILS_STAFF_GROUPS"),
              key: "name",
              align: "left",
            },
          ]}
          rows={props?.data?.content?.staffGroups}
          noResultsText={props.t("NO_STAFF_GROUP_RESTIRCTIONS_MESSAGE")}
        />
      </SectionComponent>

      {/* Service length */}
      <SectionComponent
        className="gs-shadow"
        title={<h5>{props.t("ALERT_DETAILS_SERVICE_LENGTH")}</h5>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(6)}>
            {props.t("EDIT")}
          </Button>
        }
      >
        <AlertServiceLengthView data={props.data.content} />
      </SectionComponent>
      {isEditModalOpen && (
        <AlertsEditModal
          clientId={props.clientId}
          defaultStep={modalStep}
          editing
          container={props.data}
          onCancel={toggleEditModal}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  specialContactTypes: sharedSelectors.getSpecialContactTypes(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
  getCurrentAlert: actionCreator.GetAlert,
  getSchema: actionCreator.GetAlertSchema,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertDetailsView);
